import type { Nullable } from "cy-util";
import type { FunctionComponent, ReactNode } from "react";
import { useEffect, useRef } from "react";
import { Disclosure } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import { cva, cx } from "class-variance-authority";
import { create } from "zustand";
import { useBreakpoint, useRefValue } from "../../hooks";
import * as headless from "@headlessui/react";
import { transitionVariants } from "../Transitions/transitionVariants";
import type { Icon } from "../Icons";
import { NextLink } from "../Links";
// const SIDE_NAV_EXPANDED_KEY = "SIDE_NAV_EXPANDED_KEY";
// interface ContextProps {
//   expanded: boolean;
//   expand: () => void;
// }
export interface SideNavState {
  expanded: boolean;
  expand: () => void;
  collapse: () => void;
  toggle: () => void;
  expandedDropdowns: Record<string, boolean>;
  toggleDropdown: (name: string) => void;
}

const useSideNavStore = create<SideNavState>((set) => ({
  expanded: false,
  expandedDropdowns: {},
  expand: () => {
    set((state) => ({ ...state, expanded: true }));
  },
  collapse: () => {
    set((state) => ({ ...state, expanded: false }));
  },
  toggle: () => {
    set((state) => ({ ...state, expanded: !state.expanded }));
  },
  toggleDropdown: (name: string) => {
    set((state) => ({
      ...state,
      expandedDropdowns: {
        ...state.expandedDropdowns,
        [name]: !state.expandedDropdowns[name],
      },
    }));
  },
}));

//const Context = createContext<ContextProps | Nil>(null);
// function useSideNavContext() {
//   const context = useContext(Context);
//   if (!context) throw new Error("Context not set for sidenav");
//   return context;
// }
interface SideNavProps {
  children: ({ expanded }: { expanded: boolean }) => ReactNode;
  className?: string;
}

const sidenavStyles = cva(
  [
    " pt-6 transition-all duration-200 text-gray-600 z-50",
    "overflow-x-hidden fixed bg-white bottom-0 top-14",
    "flex flex-col lg:shadow-none lg:border-r",
  ],
  {
    variants: {
      expanded: {
        true: "w-60 shad nav-menu--open shadow-lg ",
        false: "w-16 border-r",
      },
    },
  },
);

function SideNavRoot({ children, className }: SideNavProps) {
  const { ref } = useRefValue<HTMLElement>();
  const { breakpoint } = useBreakpoint();
  const autoCloseRef = useRef<boolean>(false);
  autoCloseRef.current = ["", "sm", "md"].includes(breakpoint);

  const { expanded, expand, collapse } = useSideNavStore();

  useEffect(() => {
    if (autoCloseRef.current) {
      collapse();
    }
  }, [collapse]);

  function expandToggle() {
    if (expanded) {
      collapse();
    } else {
      expand();
    }
  }

  return (
    <div className={cx("mr-16 transition-all", expanded && "lg:mr-64")}>
      <nav className={sidenavStyles({ className, expanded })} ref={ref}>
        <ul className="flex-grow px-2">{children({ expanded })}</ul>
        <button
          className={cx(
            "flex items-center bg-gray-100 py-5 hover:bg-gray-200",
            expanded && "justify-end px-5",
            !expanded && "justify-center",
          )}
          onClick={expandToggle}
        >
          <ChevronDoubleRightIcon
            className={cx("h-5 w-5", expanded && "rotate-180")}
          />
        </button>
      </nav>
    </div>
  );
}

const menuItemClasses = (active: Nullable<boolean>) =>
  cx(
    "px-2 py-2 flex items-center gap-4 w-full hover:text-primary-600 rounded outline-0 focus:ring-link-ring-focus focus:ring-[length:var(--link-ring-width-focus)]",
    active && "text-primary-500",
  );

interface LinkNavItemProps {
  href: string;
  label: string;
  Icon: any;
  className?: string;
  active?: boolean;
}

function LinkNavItem({
  className,
  active,
  label,
  Icon,
  href,
}: LinkNavItemProps) {
  const css = cx(className);
  const { expanded } = useSideNavStore();
  return (
    <li className={css}>
      <NextLink
        aria-label={label}
        className={menuItemClasses(active)}
        href={href}
      >
        <span className={cx("ml-1")}>
          <Icon className="h-5" />
        </span>{" "}
        <span
          className={cx(
            "",
            !expanded && "opacity-0",
            expanded && "opacity-100",
          )}
        >
          {label}
        </span>
      </NextLink>
    </li>
  );
}

interface ButtonLinkItemProps {
  className?: string;
  Icon: Icon;
  label: string;
  onClick: () => void;
}

/**
 *
 */
const ButtonNavItem: FunctionComponent<ButtonLinkItemProps> = ({
  className,
  Icon,
  label,
  onClick,
}) => {
  const css = cx(className);

  const { expanded } = useSideNavStore();
  return (
    <li className={css}>
      <button
        className={cx(
          "focus:ring-link-ring-focus hover:text-primary-600 flex w-full gap-4 rounded px-2 py-2 outline-0 focus:ring-[length:var(--link-ring-width-focus)]",
        )}
        onClick={onClick}
      >
        <span className={cx("ml-1 transition-all")}>
          <Icon className="h-5" />
        </span>{" "}
        <span
          className={cx(
            "transition-all",
            !expanded && "opacity-0",
            expanded && "opacity-100",
          )}
        >
          {label}
        </span>
      </button>
    </li>
  );
};

interface DropdownMenuProps {
  className?: string;
  children: ReactNode;
  active?: boolean;
  label: string;
  Icon: any;
}

/**
 *
 */
const DropdownMenu: FunctionComponent<DropdownMenuProps> = ({
  className,
  active,
  children,
  Icon,
  label,
}) => {
  const css = cx(className);

  const { expanded, expand, toggleDropdown, expandedDropdowns } =
    useSideNavStore();
  const disclosureOpen = Boolean(expandedDropdowns[label]) || active;
  function onClick(e: any) {
    expand();
    toggleDropdown(label);
    // prevent when expanding to close open sub menu.
    if (!expanded) {
      e.preventDefault();
    }
  }
  return (
    <Disclosure as="li" className={css} defaultOpen={disclosureOpen}>
      <Disclosure.Button
        className={cx(menuItemClasses(active), "relative")}
        onClick={onClick}
      >
        {({ open }) => (
          <>
            <span className={cx("ml-1 transition-all")}>
              <Icon className="h-5" />
            </span>{" "}
            <span className={cx(expanded ? "block" : "hidden")}>{label}</span>
            <span>
              <ChevronRightIcon
                className={cx(
                  "absolute top-3 right-1 h-5 w-5 text-gray-500 transition-all",
                  expanded ? "opacity-100" : "opacity-0",
                  open && "rotate-90",
                )}
              />
            </span>
          </>
        )}
      </Disclosure.Button>
      <headless.Transition {...transitionVariants.transition2}>
        <Disclosure.Panel className={cx(expanded ? "block" : "hidden")}>
          {() => {
            return <ul>{children}</ul>;
          }}
        </Disclosure.Panel>
      </headless.Transition>
    </Disclosure>
  );
};

interface DropDownMenuItemProps {
  className?: string;
  active?: boolean;
  label: string;
  href: string;
}

function DropDownMenuItem({
  className,
  label,
  href,
  active,
}: DropDownMenuItemProps) {
  const css = cx(className);
  return (
    <li className={cx(css)}>
      <NextLink
        className={cx(
          menuItemClasses(active),
          "pl-12 text-sm whitespace-nowrap",
        )}
        href={href}
        title={label}
      >
        {label}
      </NextLink>
    </li>
  );
}

export const SideNav = Object.assign(SideNavRoot, {
  LinkNavItem,
  ButtonNavItem,
  DropdownMenu,
  DropDownMenuItem,
});
